import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft, Dribbble, Zap, ClipboardList, Castle, ArrowRight, Mail, Phone } from "lucide-react";
import api from "@/lib/api";
import { PERSONA_OPTIONS, getPersonaBySlug } from "@/lib/personas";

export default function Auth() {
  const [searchParams] = useSearchParams();
  const initTab = searchParams.get("tab") === "register" ? "register" : "login";
  const personaSlug = searchParams.get("persona") || "";
  const referralCode = searchParams.get("ref") || "";
  const redirectTo = searchParams.get("redirect") || "";
  const emailVerified = searchParams.get("verified") === "true";
  const [tab, setTab] = useState(initTab);
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "USER" });
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [loginError, setLoginError] = useState(""); // tracks wrong password state
  const [registerSuccess, setRegisterSuccess] = useState(false); // tracks successful passwordless registration
  const [registeredEmail, setRegisteredEmail] = useState(""); // email used for registration
  const { user, loginWithEmail, register, setUser, checkAuth } = useAuth();
  const navigate = useNavigate();
  const selectedPersona = getPersonaBySlug(personaSlug);
  const showPersonaHub = tab === "login" && !selectedPersona;
  
  // OTP State
  const [otpMode, setOtpMode] = useState(null); // null, 'viber', 'email'
  const [otpStep, setOtpStep] = useState(1); // 1=input, 2=code
  const [otpPhone, setOtpPhone] = useState("");
  const [otpEmail, setOtpEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpSending, setOtpSending] = useState(false);

  useEffect(() => {
    // Show toast for verified email
    if (emailVerified) {
      toast.success("Email verified successfully! You can now log in.");
    }
  }, [emailVerified]);

  // Reset register form when tab changes
  useEffect(() => {
    setShowRegisterForm(false);
  }, [tab]);

  useEffect(() => {
    setOtpMode(null);
    setOtpStep(1);
    setOtpCode("");
  }, [personaSlug]);

  useEffect(() => {
    if (user) {
      if (redirectTo) { navigate(redirectTo); return; }
      if (["PLATFORM_ADMIN", "BACKOFFICE"].includes(user.role)) navigate("/admin");
      else if (user.role === "CONDO_ADMIN") navigate("/condo");
      else if (user.role === "PROVIDER") navigate("/provider");
      else if (user.is_coordinator) navigate("/recruitment");
      else if (user.is_coach) navigate("/coach");
      else navigate("/dashboard");
    }
  }, [user, navigate, redirectTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === "login") {
        if (!form.email.trim()) { toast.error("Please enter your email address."); setLoading(false); return; }
        if (!form.password) { toast.error("Please enter your password."); setLoading(false); return; }
        const res = await loginWithEmail(form.email, form.password, selectedPersona?.key || null);
        if (res.needs_persona_selection) {
          toast.error("Please choose a persona login first.");
          setLoading(false);
          return;
        }
        toast.success("Welcome back!");
      } else {
        // Passwordless registration for Players/Coaches
        if (!form.name.trim()) { toast.error("Please enter your full name."); setLoading(false); return; }
        if (!form.email.trim()) { toast.error("Please enter a valid email address."); setLoading(false); return; }
        
        // Call API directly for passwordless registration
        const res = await api.post("/auth/register", {
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          role: form.role,
          referral_code: referralCode || undefined,
        });
        
        if (res.data.needs_verification) {
          // Passwordless flow - show success message
          setRegisteredEmail(res.data.email || form.email);
          setRegisterSuccess(true);
          toast.success("Account created! Check your email to verify and set your password.");
        } else {
          // Legacy flow with password
          toast.success("Account created successfully! Welcome to SetPlay.");
        }
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "";
      if (tab === "login" && msg.toLowerCase().includes("incorrect password")) {
        setLoginError(msg);
      } else {
        setLoginError("");
      }
      if (msg) { toast.error(msg); }
      else if (err.code === "ERR_NETWORK") { toast.error("Unable to connect to the server. Please check your internet connection and try again."); }
      else { toast.error("An unexpected error occurred. Please try again."); }
    } finally { setLoading(false); }
  };

  // ═══════════════════════════════════════════════════════
  // VIBER OTP
  // ═══════════════════════════════════════════════════════
  const sendViberOtp = async () => {
    if (!otpPhone.trim()) { toast.error("Please enter your phone number"); return; }
    setOtpSending(true);
    try {
      let phone = otpPhone.trim();
      if (!phone.startsWith("+")) phone = "+63" + phone.replace(/^0/, "");
      await api.post("/auth/viber-otp/send", { phone_number: phone });
      setOtpPhone(phone);
      setOtpStep(2);
      toast.success("OTP sent via Viber!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send OTP. Please try again.");
    } finally { setOtpSending(false); }
  };

  const verifyViberOtp = async () => {
    if (!otpCode.trim() || otpCode.length < 4) { toast.error("Please enter the OTP code"); return; }
    setOtpSending(true);
    try {
      const res = await api.post("/auth/viber-otp/login", { phone_number: otpPhone, code: otpCode });
      if (res.data.session_token) {
        const { setSessionToken } = await import("@/lib/api");
        setSessionToken(res.data.session_token);
      }
      setUser(res.data);
      await checkAuth();
      toast.success("Welcome!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid OTP code. Please try again.");
    } finally { setOtpSending(false); }
  };

  // ═══════════════════════════════════════════════════════
  // EMAIL OTP
  // ═══════════════════════════════════════════════════════
  const sendEmailOtp = async () => {
    if (!otpEmail.trim()) { toast.error("Please enter your email address"); return; }
    if (!otpEmail.includes("@")) { toast.error("Please enter a valid email address"); return; }
    setOtpSending(true);
    try {
      await api.post("/auth/email-otp/send", { email: otpEmail.trim().toLowerCase() });
      setOtpStep(2);
      toast.success("Verification code sent to your email!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send verification code. Please try again.");
    } finally { setOtpSending(false); }
  };

  const verifyEmailOtp = async () => {
    if (!otpCode.trim() || otpCode.length < 6) { toast.error("Please enter the 6-digit code"); return; }
    setOtpSending(true);
    try {
      const res = await api.post("/auth/email-otp/verify", { email: otpEmail, code: otpCode });
      if (res.data.session_token) {
        const { setSessionToken } = await import("@/lib/api");
        setSessionToken(res.data.session_token);
      }
      setUser(res.data);
      await checkAuth();
      toast.success("Welcome!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid verification code. Please try again.");
    } finally { setOtpSending(false); }
  };

  const resendEmailOtp = async () => {
    setOtpSending(true);
    try {
      await api.post("/auth/email-otp/resend", { email: otpEmail });
      toast.success("New verification code sent!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Please wait before requesting a new code.");
    } finally { setOtpSending(false); }
  };

  const resetOtpMode = () => {
    setOtpMode(null);
    setOtpStep(1);
    setOtpCode("");
    setOtpPhone("");
    setOtpEmail("");
  };

  const goToPersonaLogin = (slug) => {
    const params = new URLSearchParams();
    params.set("persona", slug);
    if (redirectTo) params.set("redirect", redirectTo);
    navigate(`/login?${params.toString()}`);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative flex-col">
        <img src="https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=1000&q=80"
          alt="Sports" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-night-asphalt/80" />
        <div className="relative z-10 flex flex-col justify-between h-full p-12">
          <div />
          <div>
            <h2 className="font-heading text-5xl text-white font-bold uppercase leading-tight mb-3">
              Laro. Galaw. <span className="text-[#FBBF24]">Panalo.</span>
            </h2>
            <p className="text-white/80 text-lg max-w-sm mb-2">All-in-one sports platform ng Pilipinas.</p>
            <p className="text-white/50 text-sm max-w-sm">Book venues, courts, join events, hire coaches and pros, build communities — lahat nasa iisang app.</p>
          </div>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex flex-col justify-center px-6 md:px-16 lg:px-20 py-12 bg-white overflow-y-auto">
        <button data-testid="back-to-home" onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 self-start">
          <ArrowLeft className="w-4 h-4" />Back to home
        </button>

        <div className="max-w-sm w-full">
          <div className="flex items-center gap-3 mb-1">
            <img src="/logo.png" alt="SetPlay" className="h-12 w-12" />
            <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">SetPlay</h1>
          </div>
          <p className="text-muted-foreground text-sm mb-6">Book. Play. Connect.</p>

          {showPersonaHub && (
            <div className="space-y-4 mb-6" data-testid="persona-login-hub">
              <div>
                <p className="text-xs font-heading uppercase tracking-[0.2em] text-muted-foreground mb-3">Choose your login</p>
                <div className="space-y-3">
                  {PERSONA_OPTIONS.map((persona) => {
                    const iconMap = { player: Dribbble, provider: Castle, coach: Zap };
                    const Icon = iconMap[persona.slug] || Dribbble;
                    return (
                      <button
                        key={persona.key}
                        type="button"
                        data-testid={`persona-login-${persona.slug}`}
                        onClick={() => goToPersonaLogin(persona.slug)}
                        className="w-full p-4 border-2 border-slate-200 hover:border-court-orange hover:bg-court-orange/5 transition-all flex items-center gap-4 text-left group"
                      >
                        <div className="w-12 h-12 rounded-full bg-slate-100 group-hover:bg-court-orange/10 flex items-center justify-center transition-colors">
                          <Icon className="w-6 h-6 text-court-orange" />
                        </div>
                        <div className="flex-1">
                          <p className="font-heading font-bold text-sm uppercase tracking-wide">{persona.label}</p>
                          <p className="text-xs text-muted-foreground">{persona.description}</p>
                        </div>
                        <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-court-orange transition-colors" />
                      </button>
                    );
                  })}
                </div>
              </div>
              <Button variant="outline" onClick={() => navigate("/admin-access")} className="w-full rounded-full" data-testid="persona-login-admin-link">
                Platform Admin Login
              </Button>
              <Button variant="ghost" onClick={() => setTab("register")} className="w-full rounded-full" data-testid="persona-login-register-link">
                Need an account? Register here
              </Button>
            </div>
          )}

          {/* OTP Login Options */}
          {!showPersonaHub && !otpMode && tab === "login" && selectedPersona?.slug === "player" && (
            <div className="space-y-2 mb-4">
              <Button variant="outline" onClick={() => setOtpMode('email')}
                className="w-full h-11 rounded-none border-2 text-sm font-heading uppercase tracking-wide hover:border-court-orange hover:text-court-orange" 
                data-testid="email-otp-btn">
                <Mail className="w-4 h-4 mr-2" />
                Login with Email OTP
              </Button>
              <Button variant="outline" onClick={() => setOtpMode('viber')}
                className="w-full h-11 rounded-none border-2 text-sm font-heading uppercase tracking-wide hover:border-[#7360F2] hover:text-[#7360F2]" 
                data-testid="viber-otp-btn">
                <Phone className="w-4 h-4 mr-2" />
                Login with Viber OTP
              </Button>
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════ */}
          {/* EMAIL OTP FORM */}
          {/* ═══════════════════════════════════════════════════════ */}
          {otpMode === 'email' && selectedPersona?.slug === 'player' && (
            <div className="space-y-4 mb-6" data-testid="email-otp-form">
              <div className="flex items-center justify-between">
                <div className="p-3 bg-court-orange/5 border border-court-orange/20 rounded-lg flex-1">
                  <p className="text-xs text-court-orange font-medium flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Email Verification
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-1">We'll send a verification code to your email</p>
                </div>
                <Button variant="ghost" size="sm" onClick={resetOtpMode} className="ml-2 text-xs">Cancel</Button>
              </div>
              
              {otpStep === 1 ? (
                <>
                  <div>
                    <Label className="text-xs font-heading uppercase tracking-wider">Email Address</Label>
                    <Input data-testid="email-otp-input" type="email" value={otpEmail}
                      onChange={e => setOtpEmail(e.target.value)} placeholder="you@email.com"
                      className="h-11 rounded-none border-2 mt-1" />
                  </div>
                  <Button onClick={sendEmailOtp} disabled={otpSending} data-testid="send-email-otp-btn"
                    className="w-full h-12 text-white rounded-full font-heading uppercase tracking-wide bg-court-orange hover:bg-court-orange-hover shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
                    {otpSending ? "Sending..." : "Send Verification Code"}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </>
              ) : (
                <>
                  <div>
                    <Label className="text-xs font-heading uppercase tracking-wider">Enter Verification Code</Label>
                    <Input data-testid="email-code-input" type="text" value={otpCode} maxLength={6}
                      onChange={e => setOtpCode(e.target.value.replace(/\D/g, ""))} placeholder="6-digit code"
                      className="h-11 rounded-none border-2 mt-1 text-center text-lg tracking-[0.5em] font-mono" />
                    <p className="text-[10px] text-muted-foreground mt-1">Code sent to {otpEmail}</p>
                  </div>
                  <Button onClick={verifyEmailOtp} disabled={otpSending} data-testid="verify-email-otp-btn"
                    className="w-full h-12 bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase tracking-wide shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
                    {otpSending ? "Verifying..." : "Verify & Login"}
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="ghost" onClick={() => { setOtpStep(1); setOtpCode(""); }} className="flex-1 text-sm">
                      Change email
                    </Button>
                    <Button variant="ghost" onClick={resendEmailOtp} disabled={otpSending} className="flex-1 text-sm">
                      Resend code
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════ */}
          {/* VIBER OTP FORM */}
          {/* ═══════════════════════════════════════════════════════ */}
          {otpMode === 'viber' && selectedPersona?.slug === 'player' && (
            <div className="space-y-4 mb-6" data-testid="viber-otp-form">
              <div className="flex items-center justify-between">
                <div className="p-3 bg-[#7360F2]/5 border border-[#7360F2]/20 rounded-lg flex-1">
                  <p className="text-xs text-[#7360F2] font-medium flex items-center gap-2">
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#7360F2"><path d="M11.4 0C9.5.1 5 .5 3 3.2 1.3 5.6 1 9 1 12.3c0 3 .7 5.4 2 7.1l-.3 3.3 3.2-1c2 .6 4 .8 5 .8h.3c2-.1 6.2-.6 8.4-3.3 1.6-2.4 1.9-5.8 1.9-8.9 0-3.2-.3-5.5-2-7.9C17.4.5 13.3.1 11.4 0z"/></svg>
                    Viber Login
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-1">We'll send a code to your Viber app</p>
                </div>
                <Button variant="ghost" size="sm" onClick={resetOtpMode} className="ml-2 text-xs">Cancel</Button>
              </div>
              
              {otpStep === 1 ? (
                <>
                  <div>
                    <Label className="text-xs font-heading uppercase tracking-wider">Phone Number</Label>
                    <Input data-testid="viber-phone-input" type="tel" value={otpPhone}
                      onChange={e => setOtpPhone(e.target.value)} placeholder="+63 9XX XXX XXXX"
                      className="h-11 rounded-none border-2 mt-1" />
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Make sure Viber is installed and active on this number
                    </p>
                  </div>
                  <Button onClick={sendViberOtp} disabled={otpSending} data-testid="send-viber-otp-btn"
                    className="w-full h-12 text-white rounded-full font-heading uppercase tracking-wide bg-[#7360F2] hover:bg-[#5B48D0] shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
                    {otpSending ? "Sending..." : "Send OTP via Viber"}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </>
              ) : (
                <>
                  <div>
                    <Label className="text-xs font-heading uppercase tracking-wider">Enter OTP Code</Label>
                    <Input data-testid="viber-code-input" type="text" value={otpCode} maxLength={6}
                      onChange={e => setOtpCode(e.target.value.replace(/\D/g, ""))} placeholder="6-digit code"
                      className="h-11 rounded-none border-2 mt-1 text-center text-lg tracking-[0.5em] font-mono" />
                    <p className="text-[10px] text-muted-foreground mt-1">Code sent to {otpPhone}</p>
                  </div>
                  <Button onClick={verifyViberOtp} disabled={otpSending} data-testid="verify-viber-otp-btn"
                    className="w-full h-12 bg-[#7360F2] hover:bg-[#5B48D0] text-white rounded-full font-heading uppercase tracking-wide shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
                    {otpSending ? "Verifying..." : "Verify & Login"}
                  </Button>
                  <Button variant="ghost" onClick={() => { setOtpStep(1); setOtpCode(""); }} className="w-full text-sm">
                    Change phone number
                  </Button>
                </>
              )}
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════ */}
          {/* EMAIL/PASSWORD FORM */}
          {/* ═══════════════════════════════════════════════════════ */}
          {!otpMode && !showPersonaHub && (
          <>
          <div className="relative mb-5">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
            <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-3 text-muted-foreground font-heading tracking-wider">or use password</span></div>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="w-full mb-5 bg-slate-100 rounded-none h-11">
              <TabsTrigger data-testid="login-tab" value="login" className="flex-1 rounded-none font-heading uppercase tracking-wide data-[state=active]:bg-white data-[state=active]:shadow-sm">Login</TabsTrigger>
              <TabsTrigger data-testid="register-tab" value="register" className="flex-1 rounded-none font-heading uppercase tracking-wide data-[state=active]:bg-white data-[state=active]:shadow-sm">Register</TabsTrigger>
            </TabsList>

            {/* Register Tab - Persona Selection */}
            <TabsContent value="register" className="mt-0">
              <div className="space-y-4">
                <div>
                  <Label className="text-xs font-heading uppercase tracking-wider mb-3 block">I am a</Label>
                  <div className="space-y-3">
                    {[
                      { value: "USER", label: "Player", desc: "Book courts, join events, find coaches", icon: Dribbble, color: "text-court-orange", path: "/auth?tab=login" },
                      { value: "COACH", label: "Coach / Pro", desc: "Host sessions, manage clients, earn", icon: Zap, color: "text-lime-volt", path: "/auth?tab=login" },
                      { value: "PROVIDER", label: "Venue Owner", desc: "List your venue, manage bookings", icon: Castle, color: "text-purple-500", path: "/register-venue" },
                    ].map(r => (
                      <button 
                        key={r.value} 
                        type="button" 
                        data-testid={`role-${r.value.toLowerCase()}`}
                        onClick={() => {
                          if (r.value === "PROVIDER") {
                            navigate("/register-venue");
                          } else {
                            setForm({...form, role: r.value});
                            setShowRegisterForm(true);
                          }
                        }}
                        className="w-full p-4 border-2 border-slate-200 hover:border-court-orange hover:bg-court-orange/5 transition-all flex items-center gap-4 text-left group"
                      >
                        <div className={`w-12 h-12 rounded-full bg-slate-100 group-hover:bg-court-orange/10 flex items-center justify-center transition-colors`}>
                          <r.icon className={`w-6 h-6 ${r.color}`} />
                        </div>
                        <div className="flex-1">
                          <p className="font-heading font-bold text-sm uppercase tracking-wide">{r.label}</p>
                          <p className="text-xs text-muted-foreground">{r.desc}</p>
                        </div>
                        <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-court-orange transition-colors" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Login Tab - Email/Password Form */}
            <TabsContent value="login" className="mt-0">
              <div className="mb-4 flex items-center justify-between" data-testid="persona-login-header">
                <div>
                  <p className="font-heading font-bold text-sm uppercase tracking-wide">{selectedPersona?.loginTitle}</p>
                  <p className="text-xs text-muted-foreground">Use your same SetPlay email and password for this persona.</p>
                </div>
                <button type="button" onClick={() => navigate(`/login${redirectTo ? `?redirect=${encodeURIComponent(redirectTo)}` : ''}`)} className="text-xs text-muted-foreground hover:text-foreground">
                  Change persona
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid={`persona-login-form-${selectedPersona?.slug || 'generic'}`}>
                <div>
                  <Label className="text-xs font-heading uppercase tracking-wider">Email</Label>
                  <Input data-testid="auth-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                    placeholder="you@email.com" required className="h-11 rounded-none border-2 mt-1" />
                </div>
                <div className="relative">
                  <Label className="text-xs font-heading uppercase tracking-wider">Password</Label>
                  <Input data-testid="auth-password" type={showPw ? "text" : "password"} value={form.password}
                    onChange={e => setForm({...form, password: e.target.value})} placeholder="Min 6 characters" required minLength={6}
                    className="h-11 rounded-none border-2 pr-10 mt-1" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-8 text-muted-foreground">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                {/* Wrong password error with reset link */}
                {loginError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-testid="login-error-reset">
                    <p className="text-sm text-red-700 mb-1">Incorrect password.</p>
                    <button
                      type="button"
                      onClick={() => navigate("/forgot-password")}
                      className="text-sm text-court-orange hover:underline font-medium"
                      data-testid="login-forgot-link-error"
                    >
                      Reset your password
                    </button>
                  </div>
                )}

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => navigate("/forgot-password")}
                    className="text-xs text-muted-foreground hover:text-court-orange transition-colors"
                    data-testid="login-forgot-link"
                  >
                    Forgot password?
                  </button>
                </div>

                <Button data-testid="auth-submit-btn" type="submit" disabled={loading}
                  className="w-full h-12 bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase tracking-wide shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-x-[1px] active:translate-y-[1px]">
                  {loading ? "Processing..." : `Login as ${selectedPersona?.label || 'User'}`}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          {/* Player/Coach Registration Form (shown after selecting role) */}
          {showRegisterForm && tab === "register" && !registerSuccess && (
            <div className="mt-6 pt-6 border-t border-slate-200" data-testid="register-form">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-bold text-sm uppercase tracking-wide">
                  Register as {form.role === "COACH" ? "Coach / Pro" : "Player"}
                </h3>
                <button 
                  type="button" 
                  onClick={() => setShowRegisterForm(false)}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  ← Back
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-xs font-heading uppercase tracking-wider">Name</Label>
                  <Input data-testid="register-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                    placeholder="Juan Dela Cruz" className="h-11 rounded-none border-2 mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-heading uppercase tracking-wider">Email</Label>
                  <Input data-testid="register-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                    placeholder="you@email.com" required className="h-11 rounded-none border-2 mt-1" />
                </div>
                
                <p className="text-xs text-muted-foreground bg-slate-50 p-3 rounded-lg">
                  After registering, you'll receive an email to verify your address and create your password.
                </p>
                
                <Button data-testid="register-submit-btn" type="submit" disabled={loading}
                  className="w-full h-12 bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase tracking-wide shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-x-[1px] active:translate-y-[1px]">
                  {loading ? "Creating Account..." : "Create Account"}
                </Button>
              </form>
            </div>
          )}
          
          {/* Registration Success State */}
          {registerSuccess && (
            <div className="mt-6 pt-6 border-t border-slate-200" data-testid="register-success">
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="font-heading font-bold text-lg mb-2">Check Your Email!</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  We've sent a verification link to:
                </p>
                <p className="font-semibold text-sm mb-6">{registeredEmail}</p>
                <p className="text-xs text-muted-foreground mb-6">
                  Click the link in your email to verify your account and set your password.
                </p>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setRegisterSuccess(false);
                    setShowRegisterForm(false);
                    setTab("login");
                  }}
                  className="rounded-full"
                >
                  Go to Login
                </Button>
              </div>
            </div>
          )}
          </>
          )}

        </div>
      </div>
    </div>
  );
}
